import { useState, useEffect, FC } from "react";

import { usePostAnalyticsEvent } from "../../hooks/usePostAnalyticsEvent";
import useRecentQueries from "../../hooks/useRecentQueries";
import useResponsive from "../../hooks/useResponsive";
import { useAppDispatch } from "../../redux/hooks";
import { useFeatureSearchParams } from "../../hooks/useFeatureSearchParams";
import { useQuickSearchTags } from "../../hooks/useQuickSearchTags";

import { AnalyticsEvent } from "../../utils/analyticsTypes";
import { push } from "../../utils/navigate";
import { QueryParams, QueryParamBinary } from "../../utils/types";

import { RECENT_SEARCHES_KEY } from "../../constants";

import SearchTags, {
  Category,
  CategoryType,
} from "../../components/SearchTags/SearchTags";

import styles from "./SearchTagsContainer.module.scss";

interface Props {
  mobile: boolean;
  onClose: () => void;
}

const SearchTagsContainer: FC<Props> = ({ onClose, mobile }) => {
  const dispatch = useAppDispatch();
  const { Mobile } = useResponsive();

  const { query, searchTagType, isQuickSearch } = useFeatureSearchParams();

  const { quickSearchTags, getQuickSearchTagFromId } = useQuickSearchTags();

  const { addRecentQuery } = useRecentQueries(RECENT_SEARCHES_KEY);

  const { logAnalyticsEvent } = usePostAnalyticsEvent();

  const [activeQuickSearchTag, setActiveQuickSearchTag] =
    useState<Category | null>(null);

  const handleOnClick = (search: Category) => {
    if (activeQuickSearchTag === search) {
      onClose();
    } else {
      setActiveQuickSearchTag(search);
      addRecentQuery(String(search.id), {
        quickSearch: true,
        bespoke: search.type === CategoryType.BESPOKE,
      });
      logAnalyticsEvent({
        event_type: AnalyticsEvent.SEARCH_QUERY_SEND,
        event_data: {
          query: getQuickSearchTagFromId(search.id, search.type, true),
          quick_search: true,
          quick_search_id: search.id,
        },
      });

      dispatch(
        push({
          pathOrLocation: "/search",
          newQueryParams: {
            [QueryParams.QUERY]: search?.id,
            [QueryParams.SEARCH_TAG_TYPE]: search?.type,
            [QueryParams.QUICK_SEARCH]: QueryParamBinary.ENABLED,
          },
          discardParams: [QueryParams.FEATURE_ID, QueryParams.NAME_SEARCH],
        }),
      );
    }
  };

  useEffect(() => {
    if (isQuickSearch && query && searchTagType) {
      const activeQSTag = quickSearchTags.find(
        (tag) => String(tag.id) === query && tag.type === searchTagType,
      );
      if (activeQSTag) setActiveQuickSearchTag(activeQSTag);
    } else {
      setActiveQuickSearchTag(null);
    }
  }, [quickSearchTags, isQuickSearch, query, searchTagType]);
  return quickSearchTags.length ? (
    // HACK: need to add dir="ltr" to the parent div to ensure the tags work correctly with the slider
    <div className={styles.quickSearchTags} dir="ltr">
      <SearchTags
        dataQA="search-tags"
        enableArrows={!Mobile}
        data={quickSearchTags}
        active={activeQuickSearchTag}
        onClick={handleOnClick}
        mobile={mobile}
      />
    </div>
  ) : null;
};

export default SearchTagsContainer;
